import {
  DropdownOptions,
  Icon,
  ModalProvider,
  toast,
  TooltipPopup,
  ViewerContext,
} from "dash-ui";
import {
  IdxFeed,
  IdxSettings,
  useCreateIdxFeedMutation,
  useCreateIdxSettingsMutation,
  useDeleteIdxFeedMutation,
  useGetIdxFeedListQuery,
  useGetIdxSettingsQuery,
  useUpdateIdxSettingsMutation,
} from "gql/generated.graphql";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiFolder,
  FiGlobe,
  FiHome,
  FiLoader,
  FiPlus,
  FiSearch,
  FiSettings,
  FiTrash,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { DeleteIdxFeedModal } from "v2/components/idx/delete-idx-feed-modal";
import { BrandingInput } from "../../../gql/generated.graphql";
import {
  IdxSettingsFormData,
  IdxSettingsModal,
} from "v2/components/idx/idx-settings-modal";
import {
  NewIdxFeedFormData,
  NewIdxFeedModal,
} from "v2/components/idx/new-idx-feed-modal";

type ModalStates = {
  newFeedOpen: boolean;
  confirmDeleteOpen: boolean;
  idxSettingsOpen: boolean;
};

type QueryVariables = {
  pagination: {
    skip: number;
    limit: number;
  };
  filter: {
    nameSearch?: string;
  };
  sort: {
    fieldName: string;
    sortOrder: number;
  };
};

export function IdxPage() {
  // States to handle pagination and filtering
  const [queryVariables, setQueryVariables] = useImmer<QueryVariables>({
    pagination: { limit: 12, skip: 0 },
    filter: {},
    sort: {
      fieldName: "createdAt",
      sortOrder: -1,
    },
  });

  // State to handle the delete click data
  const [selectedToDelete, setSelectedToDelete] = useState<IdxFeed | null>(
    null
  );

  // React state using Immer to handle modal states easily
  const [modalStates, setModalStates] = useImmer<ModalStates>({
    newFeedOpen: false,
    confirmDeleteOpen: true,
    idxSettingsOpen: false,
  });

  // Hooks
  const loggedUser = useContext(ViewerContext);
  const navigate = useNavigate();

  // Queries, Mutations and derived states
  const getIdxSettingsQuery = useGetIdxSettingsQuery();
  const idxSettings = getIdxSettingsQuery.data?.getIdxSettings as IdxSettings;
  const getIdxFeedsQuery = useGetIdxFeedListQuery({
    variables: { input: queryVariables },
    fetchPolicy: "network-only",
  });
  const feeds =
    (getIdxFeedsQuery.data?.getIdxFeedList.collection as IdxFeed[]) || [];
  const totalFeeds = getIdxFeedsQuery.data?.getIdxFeedList.totalCount || 0;
  const [updateIdxSettingsMutation] = useUpdateIdxSettingsMutation();
  const [createIdxSettingsMutation] = useCreateIdxSettingsMutation();
  const [createIdxFeedMutation] = useCreateIdxFeedMutation();
  const [deleteIdxFeedMutation] = useDeleteIdxFeedMutation();

  // Derived states for pagination

  const lastPage = Math.ceil(
    (totalFeeds || queryVariables.pagination.limit) /
      queryVariables.pagination.limit
  );
  const allPages =
    new Array(lastPage || 1).fill(null).map((_, index) => index + 1) || [];
  const selectedPage =
    queryVariables.pagination.skip / queryVariables.pagination.limit + 1;
  const previousPages = allPages.slice(0, selectedPage - 1);
  const nextPages = allPages.slice(selectedPage, allPages.length);
  const filteredPreviousPages =
    nextPages.length < 3
      ? previousPages.slice(-6 + nextPages.length)
      : previousPages.slice(-3);
  const filteredNextPages =
    previousPages.length < 3
      ? nextPages.slice(0, 6 - previousPages.length)
      : nextPages.slice(0, 3);
  const pages = [...filteredPreviousPages, selectedPage, ...filteredNextPages];

  // Pagination functions

  const onNextPage = useCallback(() => {
    if (selectedPage === lastPage) return;
    setQueryVariables((draft) => {
      draft.pagination.skip = draft.pagination.skip + draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, [selectedPage, lastPage]);

  const onPreviousPage = useCallback(() => {
    if (selectedPage === 1) return;
    setQueryVariables((draft) => {
      draft.pagination.skip = draft.pagination.skip - draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, [selectedPage]);

  const onGoToPage = useCallback((page: number) => {
    setQueryVariables((draft) => {
      draft.pagination.skip =
        page * draft.pagination.limit - draft.pagination.limit;
      localStorage.setItem(
        "DASH_CMS_BROKERS_TABLE_FILTERS",
        JSON.stringify(draft)
      );
    });
  }, []);

  // Handle save IDX Settings functionality
  async function saveSettings(data: IdxSettingsFormData) {
    if (!idxSettings) {
      await createIdxSettingsMutation({
        variables: {
          websiteDomain: data.websiteDomain,
          branding: data.branding as BrandingInput,
        },
      });
      toast.success("IDX Settings created successfully.");
      getIdxSettingsQuery.refetch();
      return;
    }
    await updateIdxSettingsMutation({
      variables: {
        input: {
          websiteDomain: data.websiteDomain,
          branding: data.branding as BrandingInput,
          idxSettingsId: idxSettings._id,
        },
      },
    });
    toast.success("IDX Settings updated successfully.");
    getIdxFeedsQuery.refetch();
    return;
  }

  // Handle create IDX Feed functionality
  async function createIdxFeed(data: NewIdxFeedFormData) {
    await createIdxFeedMutation({
      variables: {
        input: {
          name: data.name,
          type: data.type,
        },
      },
    });
    toast.success("IDX Feed created successfully.");
    getIdxFeedsQuery.refetch();
  }

  // Handle delete IDX Feed functionality
  async function deleteIdxFeed() {
    await deleteIdxFeedMutation({
      variables: { idxFeedId: selectedToDelete?._id },
    });
    toast.success("IDX Feed deleted successfully.");
    getIdxFeedsQuery.refetch();
  }

  // Redirect user if has no active subscription
  if (loggedUser?.subscription?.subscriptionProduct?.name !== "DASH Exchange") {
    return (
      <>
        <ModalProvider>
          <>
            <div className="flex flex-col h-full w-full text-slate-800">
              <header className="flex">
                <h1 className="font-bold text-xl">IDX Integration</h1>
              </header>

              <main className="flex flex-col items-center4">
                <p className="text-xl">
                  Get access to Exchange Feeds with the DASH Exchange plan!
                </p>
                <button
                  onClick={() => {
                    navigate("/profile?tab=BILLING");
                  }}
                  className="bg-sky-600 text-white rounded px-4 h-10 max-w-max"
                >
                  Manage your subscription
                </button>
              </main>
            </div>
            <DropdownOptions />
            <TooltipPopup />
          </>
        </ModalProvider>
      </>
    );
  }

  // HTML Component
  return (
    <>
      <ModalProvider>
        <>
          <div className="flex flex-col w-full h-full gap-6 text-slate-800">
            {/* Breadcrumbs */}
            <div className="flex">
              <div className="flex items-center gap-2 text-slate-500">
                <Icon name="dashExchange" size={16} />
                <span>Exchange</span>
              </div>
            </div>

            {/* Header */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex items-center flex-1">
                <input
                  type="search"
                  placeholder="Search feed by name"
                  className="h-10 rounded-lg border border-slate-300 pl-10 pr-4 w-full min-w-[150px]"
                  value={queryVariables.filter?.nameSearch || ""}
                  onChange={(event) =>
                    setQueryVariables((draft) => {
                      draft.filter = { nameSearch: event.target.value };
                    })
                  }
                />
                <FiSearch className="absolute left-4 text-slate-500" />
              </div>
              <div className="flex flex-1">
                <select
                  className="h-10 px-4 border border-slate-300 rounded-lg w-full min-w-[150px]"
                  value={`${
                    queryVariables.sort.fieldName
                  },${queryVariables.sort.sortOrder.toString()}`}
                  onChange={(event) =>
                    setQueryVariables((draft) => {
                      if (!event.target.value) return;
                      const formattedValue = event.target.value.split(",");
                      draft.sort = {
                        fieldName: formattedValue[0],
                        sortOrder: Number(formattedValue[1]),
                      };
                    })
                  }
                >
                  <option value="name,1">Name (A-Z)</option>
                  <option value="name,-1">Name (Z-A)</option>
                  <option value="createdAt,-1">Newest First </option>
                  <option value="createdAt,1">Oldest First</option>
                </select>
              </div>
              <div className="flex flex-1">
                <button
                  type="button"
                  className="bg-sky-600 rounded-lg h-10 flex items-center px-6 text-white gap-2 disabled:opacity-30 w-full min-w-[150px] justify-center"
                  disabled={!idxSettings}
                  onClick={() =>
                    setModalStates((draft) => {
                      draft.newFeedOpen = true;
                      return;
                    })
                  }
                >
                  <FiPlus />
                  <span>Add New</span>
                </button>
              </div>
              <div className="flex flex-1">
                <button
                  type="button"
                  className={`bg-sky-600 rounded-lg h-10 flex items-center px-6 text-white gap-2 w-full min-w-[150px] justify-center`}
                  onClick={() =>
                    setModalStates((draft) => {
                      draft.idxSettingsOpen = true;
                      return;
                    })
                  }
                >
                  <FiSettings />
                  <span>Settings</span>
                </button>
              </div>
            </div>

            {/* Render alert if user does not have Exchange Settings */}
            {!getIdxSettingsQuery.loading && !idxSettings?.websiteDomain && (
              <div className="flex items-center py-2 px-4 rounded-lg border border-amber-600 bg-amber-50 gap-2 text-amber-600">
                <FiAlertCircle />
                <span>
                  To begin creating eXchange feeds, please open the settings tab
                  to configure your domain name.
                </span>
              </div>
            )}

            {/* Render a loading state for the list */}
            {getIdxFeedsQuery.loading && (
              <div className="flex items-center justify-center h-96 w-full animate-pulse">
                <FiLoader className="animate-spin text-xl" />
              </div>
            )}

            {getIdxFeedsQuery.error && (
              <div className="flex items-center justify-center h-full w-full">
                <p className="text-red-600 font-bold">
                  Error loading Exchange feeds.
                </p>
              </div>
            )}

            {!getIdxFeedsQuery.loading && !feeds.length && (
              <div className="flex items-center justify-center h-96 w-full">
                <p>No results found.</p>
              </div>
            )}

            {/* List */}
            {!getIdxFeedsQuery.loading &&
              !getIdxFeedsQuery.error &&
              !!feeds.length && (
                <div className="flex flex-col gap-8">
                  {/* List Label */}
                  <p className="text-slate-500">{totalFeeds} Exchange Feeds</p>

                  {/* List */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {feeds.map((feed) => (
                      <div
                        key={feed._id}
                        className="flex flex-col shadow-lg rounded-lg border p-5 gap-3 text-slate-700"
                      >
                        <div className="flex justify-between items-center">
                          <Link
                            to={`/exchange/${feed._id}`}
                            className="font-bold text truncate"
                          >
                            {feed.name}
                          </Link>
                          <div className="flex items-center">
                            <button
                              onClick={() => {
                                setSelectedToDelete(feed);
                                setModalStates((draft) => {
                                  draft.confirmDeleteOpen = true;
                                  return;
                                });
                              }}
                              type="button"
                              className="size-8 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-500 hover:text-white"
                            >
                              <FiTrash />
                            </button>
                          </div>
                        </div>
                        {feed.type === "PORTFOLIO" && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <FiFolder />
                              <p className="text-sm capitalize">Portfolio</p>
                            </div>
                          </div>
                        )}
                        {feed.type === "SINGLE_PROPERTY" && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <FiHome />
                              <p className="text-sm capitalize">Property</p>
                            </div>
                          </div>
                        )}
                        {feed.type === "SEARCH_BAR" && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <FiSearch />
                              <p className="text-sm capitalize">Search Bar</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between">
                    <p className="text-slate-400 text-sm">
                      {`Showing ${queryVariables.pagination.skip + 1} - ${
                        queryVariables.pagination.skip +
                        (feeds.length || queryVariables.pagination.skip)
                      } of ${totalFeeds} Feeds`}
                    </p>
                    <div className="flex gap-1">
                      <button
                        disabled={selectedPage === 1}
                        className="disabled:opacity-50 h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 text-sm text-sky-600 hover:bg-sky-600/10"
                        onClick={onPreviousPage}
                      >
                        <FiChevronLeft />
                      </button>

                      {pages.map((page) => {
                        return (
                          <button
                            key={page}
                            onClick={() => onGoToPage(page)}
                            className={`h-8 w-8 flex items-center  text-sky-600 justify-center rounded-lg text-sm ${
                              selectedPage === page
                                ? "bg-sky-600 text-white font-semibold"
                                : "border border-slate-300 hover:bg-sky-600/10"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        disabled={selectedPage === lastPage}
                        className="disabled:opacity-50 h-8 w-8 flex items-center justify-center rounded-lg border border-slate-300 text-sm text-sky-600 hover:bg-sky-600/10"
                        onClick={onNextPage}
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                </div>
              )}

            {/* IDX Settings Modal */}
            {modalStates.idxSettingsOpen && (
              <IdxSettingsModal
                idxSettings={idxSettings}
                onClose={() =>
                  setModalStates((draft) => {
                    draft.idxSettingsOpen = false;
                    return;
                  })
                }
                onSave={saveSettings}
              />
            )}

            {/* New Feed Modal */}
            {modalStates.newFeedOpen && (
              <NewIdxFeedModal
                onClose={() =>
                  setModalStates((draft) => {
                    draft.newFeedOpen = false;
                    return;
                  })
                }
                onSave={createIdxFeed}
              />
            )}

            {/* Delete Confirmation Modal */}
            {modalStates.confirmDeleteOpen && (
              <DeleteIdxFeedModal
                idxFeed={selectedToDelete}
                onCancel={() =>
                  setModalStates((draft) => {
                    draft.confirmDeleteOpen = false;
                    return;
                  })
                }
                onConfirm={deleteIdxFeed}
              />
            )}
          </div>

          <DropdownOptions />
          <TooltipPopup />
        </>
      </ModalProvider>
    </>
  );
}
